import { object, lazy, string } from "yup";
import { messages } from "../../../utils/messages";
import {
  stringEnsure, stringRequired, message, name, country, date, pnr,
  numberRequired, firstDeparture, lastDeparture, booleanValue, ffpCategory,
  adults, children, infants, originAirport, destinationAirport, flightBrand,
  departureDateTime, returnDateTime, errorCode, departureDate, returnDate,
  stringMultiple, numberEnsure, passengerType, digitalServiceType,
  productTypes, monetaryValue, clickType,
  trackId, flightNumber, action, originCity, destinationCity, originCountry, destinationCountry, arrivalDate, travelType, segmentOperator,
} from '../../../utils/validations.ga4';

// Export legacy schemas: validation,displayed,error,outbound,interactive,search
export const legacy = object()
  .shape({
    action: stringRequired,
    label: stringRequired,
    productColor: stringEnsure.optional(),
    productSize: stringEnsure.optional(),  
    productVoltage: stringEnsure.optional(),
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export validation_log schema
export const validationLog = object()
  .shape({
    field: stringEnsure,
    message,
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export search_results schema
export const searchResults = object()
  .shape({
    orderBy: stringEnsure,
    status: stringEnsure,
    resultsType: stringEnsure.optional(),
    totalResults: numberRequired,
    firstDeparture,
    lastDeparture,
    hasDiscount: booleanValue,
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export search schema
export const search = object().shape({
  origin: stringEnsure,
  destination: stringEnsure,
  departureDate: date,
  returnDate: date,
  type: stringEnsure.optional(), 
  product: productTypes,
  travelType: travelType.nullable().optional(),
  segments: numberEnsure.optional(),
  field: stringEnsure,
  usingMiles: booleanValue,
  action: action.nullable(),
  status: stringEnsure.optional(), 
});

// Export filter_results schema
export const filterResults = object()
  .shape({
    criteria: stringEnsure,
    value: stringEnsure,
    filterBy: stringEnsure.optional(),
    orderBy: stringEnsure.optional(),
    totalResults: numberEnsure.optional(),
    category: stringEnsure.optional(),
    store: stringEnsure.optional(),
    milesRange: stringEnsure.optional(),
    productBrand: stringEnsure.optional(),
    productColor: stringEnsure.optional(),
    productSize: stringEnsure.optional(),  
    productVoltage: stringEnsure.optional(),
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export order_results schema
export const orderResults = object()
  .shape({
    criteria: stringEnsure,
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export error_log schema
export const errorLog = object()
  .shape({
    name,
    message,
    errorCode,
    trackId,
    redirectTo: stringEnsure,
    serviceable: booleanValue,
    passengerId: string()
      .nullable()
      .optional()
      .matches(/^(ADT|CHD|INF)_[0-9]+$/, {
        message: 'Invalid passengerId format.',
        excludeEmptyString: true,
      }),
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export response_log schema
export const responseLog = object()
  .shape({
    name,
    message,
    trackId,
    responseCode: errorCode,
    redirectTo: stringEnsure,
    serviceable: booleanValue,
    passengerId: string()
      .nullable()
      .optional()
      .matches(/^(ADT|CHD|INF)_[0-9]+$/, {
        message: 'Invalid passengerId format.',
        excludeEmptyString: true,
      }),
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export passenger_selection schema
export const passengerSelection = object().noUnknown(messages.NO_UNKNOWN);

// Export seat_selection schema
export const seatSelection = object()
  .shape({
    status: stringRequired,
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export ancillary_market schema
export const ancillaryMarket = object().noUnknown(messages.NO_UNKNOWN);

// Export change_product schema
export const changeProduct = object().noUnknown(messages.NO_UNKNOWN);

// Export funnel/checkout_step schema
export const funnelStep = object()
  .shape({
    step: stringEnsure,
    details: stringEnsure,
    segmentODA: stringEnsure
      .optional()
      .matches(/^[A-Z]{3}-[A-Z]{3}$/, {
        message: 'Invalid segmentODA format.',
        excludeEmptyString: true, 
      }),
    passengerId: string()
      .nullable()
      .optional()
      .matches(/^(ADT|CHD|INF)_[0-9]+$/, {
        message: 'Invalid passengerId format.',
        excludeEmptyString: true,
      }),
    flightNumber: flightNumber.optional(),
    status: stringEnsure.optional()
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export repricing schema
export const repricing = object()
  .shape({
    type: stringEnsure,
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export menu/shortchut schema
export const genericCategory = object()
  .shape({
    category: stringRequired,
    element: stringRequired,
    type: stringEnsure.optional(),
    label: stringEnsure.optional()
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export modal schema
export const modal = object()
  .shape({
    action: stringRequired,
    step: stringEnsure,
    name,
    section: stringEnsure.optional()
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export select, display, whatsApp schemas
export const genericEvent = object()
  .shape({
    action: stringRequired,
    label: stringRequired,
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export boardingPass schema
export const boardingPass = object()
  .shape({
    status: stringRequired,
    passengerType: passengerType,
    passengerListing: numberEnsure,
    departureDateTime,
    returnDateTime,
    originAirport,
    destinationAirport,
    flightNumber,
    passengerId: string()
      .nullable()
      .optional()
      .matches(/^(ADT|CHD|INF)_[0-9]+$/, {
        message: 'Invalid passengerId format.',
        excludeEmptyString: true,
      }),
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export upg schema
export const upg = object()
  .shape({
    order: stringEnsure,
    status: stringRequired,
    departureDateTime,
    returnDateTime,
    originAirport,
    destinationAirport,
    flightNumber,
    action: stringEnsure,
  })
  .noUnknown(messages.NO_UNKNOWN);

export const favouriteRoute = object()
  .shape({
    step: stringEnsure,
    originCity: originCity,
    originCountry: originCountry,
    destinationCity: destinationCity,
    destinationCountry: destinationCountry,
  })
  .noUnknown(messages.NO_UNKNOWN);

export const boundDetail = object()
  .shape({
    originCity: originCity,
    destinationCity: destinationCity,
    departureDate: departureDate,
    arrivalDate: arrivalDate,
    direction: stringEnsure.nullable(),
    travelType: travelType.nullable().optional(),
    segments: numberEnsure.nullable(),
    segmentOperator: segmentOperator.nullable().optional(),
  })
  .noUnknown(messages.NO_UNKNOWN);  
// Export login_success schema
export const loginSuccess = object()
  .shape({
    label: stringEnsure.optional(),
  })
  .noUnknown(messages.NO_UNKNOWN);  

// Export feature schema
export const feature = object()
  .shape({
    action: stringEnsure.nullable(), 
    name: stringEnsure.nullable(),   
    section: stringEnsure.nullable(), 
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export permissionRequest schema
export const permissionRequest = object()
  .shape({
    permission: stringEnsure.nullable(), 
    status: stringEnsure.nullable(),   
  })
  .noUnknown(messages.NO_UNKNOWN);  

// Export milestone schema
export const milestone = object()
  .shape({
    name: stringEnsure.nullable(), 
    type: stringEnsure.nullable(), 
    status: stringEnsure.nullable(),   
  })
  .noUnknown(messages.NO_UNKNOWN);
  
// Export earnedMiles schema
export const earnedMiles = object()
  .shape({
    section: stringEnsure.nullable(),
    name: stringEnsure.nullable(),
    label: stringEnsure.nullable(),
    miles: numberEnsure.nullable(),
    hasMiles: booleanValue.optional(),
  })
  .noUnknown(messages.NO_UNKNOWN);  

// Export projectedMiles schema
export const projectedMiles = object()
  .shape({
    section: stringEnsure.nullable(),
    name: stringEnsure.nullable(),
    miles: numberEnsure.nullable(),
    projectionMiles: numberEnsure.optional(),
  })
  .noUnknown(messages.NO_UNKNOWN);



// Export orderData schema
export const orderData = object()
  .shape({
    order: stringEnsure,
    status: stringEnsure,
    details: stringEnsure,
    orderDate: date,
    flightStatus: stringEnsure,
    orderCountry: country,
    orderOrigin: stringEnsure,
    orderChannel: stringEnsure,
    departureDateTime,
    returnDateTime,
    originAirport,
    destinationAirport,
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export start_form, submit_form schemas
export const formSchema = object()
  .shape({
    passengerType: passengerType,
    passengerListing: numberEnsure,
    autocomplete: booleanValue,
    details: stringEnsure,
    passengerId: string()
      .nullable()
      .optional()
      .matches(/^(ADT|CHD|INF)_[0-9]+$/, {
        message: 'Invalid passengerId format.',
        excludeEmptyString: true,
      }),
    autocompleteInput: numberEnsure.optional(),
    editInput: numberEnsure.optional(),
    autocompleteInputName: stringEnsure.nullable(),
    editInputName: stringEnsure.nullable(), 
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export digital_service schema
export const digitalService = object()
  .shape({
    serviceType: digitalServiceType,
    action: stringRequired,
    label: stringRequired,
    threadId: stringEnsure.optional()
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export tab schema
export const tab = object()
  .shape({
    category: stringRequired,
    product: productTypes,
    label: stringEnsure,
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export deeplink schema
export const deeplink = object()
  .shape({
    category: stringRequired,
    product: productTypes,
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export download schema
export const download = object()
  .shape({
    label: stringRequired,
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export accordion schema
export const accordion = object()
  .shape({
    label: stringRequired, 

    // Parámetros opcionales
    name: name.optional(), 
    flightNumber: flightNumber.optional(), 
    flightDate: departureDateTime.optional(), 
    businessUnit: stringEnsure.optional(), 
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export promo schema
export const promo = object().shape({
  product: productTypes,
  origin: stringEnsure,
  destination: stringEnsure,
  minPrice: monetaryValue,
  maxPrice: monetaryValue,
  orderBy: stringEnsure,
  totalResults: numberRequired,
  status: stringEnsure,
  usingMiles: booleanValue,
  criteria: stringEnsure,
  category: stringEnsure
}).noUnknown(messages.NO_UNKNOWN);

// Export retry schema
export const retry = object()
  .shape({
    label: stringEnsure,
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export click schema
export const click = object().shape({
  label: stringRequired,
  type: clickType,
  section: stringRequired,
}).noUnknown(messages.NO_UNKNOWN);

// Export feedback schema
export const feedback = object().shape({
  label: stringEnsure.optional(),
  message: stringEnsure.optional(),
}).noUnknown(messages.NO_UNKNOWN);

// Export orderDetail schema 
export const orderDetail = object()
  .shape({
    order: stringRequired.optional(),
    units: numberEnsure.optional(),
    paymentType: stringRequired.optional(), 
  })
  .noUnknown(messages.NO_UNKNOWN);

// Export orderContext schema
export const orderContext = object()
  .shape({
    orderContext: object()
      .shape({
        order: stringEnsure,
        pnr: pnr,
        issueDate: date,
        country: country,
        status: stringEnsure,
        migrated: booleanValue,
        origin: stringEnsure,
        originDetails: stringEnsure,
        departureDate: departureDate,
        arrivalDate: returnDate,
        ffpCategory: ffpCategory,
        adults: adults,
        children: children,
        infants: infants,
      })
      .noUnknown(messages.NO_UNKNOWN),
    itinerary: lazy((flights) => {
      const newEntries = Object.keys(flights).reduce(
        (accumulator, currentValue) => {
          return {
            ...accumulator,
            [currentValue]: object({
              origin: originAirport,
              destination: destinationAirport,
              departure: departureDateTime,
              arrival: returnDateTime,
              brand: flightBrand,
              baggageAllowances: stringEnsure,
              cabinType: stringMultiple,
              equipment: stringMultiple,
            })
              .test("key", "Invalid key name for itinerary object.", () =>
                /^flight[0-9]{1,2}$/.test(currentValue),
              )
              .noUnknown(messages.NO_UNKNOWN),
          };
        },
        {},
      );
      return object().shape(newEntries);
    }),
  })
  .noUnknown(messages.NO_UNKNOWN);
